import type { Facing } from "../movement/step.ts";
import { validatePortraitClips, type PortraitClips } from "./portrait-clips.ts";

/**
 * Character sheet descriptions (ADR-036): the frame layout, pivot and animations of one layer, read from
 * `sheet.json` beside the art so a sheet swap is a data change. Pure; the Phaser side only consumes it.
 */

/**
 * Where the build writes the character manifest, under the asset base: the game page loads its sheets from
 * it and the editor page its stand-in's art.
 */
export const CHARACTER_MANIFEST = "assets/characters/current.json";

/**
 * The four direction keys, indexed by PROTOCOL facing (0 down, 1 up, 2 left, 3 right).
 */
export const DIRECTION_NAMES = ["down", "up", "left", "right"] as const;
export type DirectionName = (typeof DIRECTION_NAMES)[number];

/**
 * The animations every sheet must provide.
 */
export const REQUIRED_ANIMS = ["idle", "walk"] as const;

/**
 * The one animation a sheet that only stands must provide: an NPC that never moves may be drawn from an idle strip
 * alone (ADR-136 decision 7.1). A caller that admits such a sheet passes this to `validateSheet`.
 */
export const STANDING_ANIMS = ["idle"] as const;

/**
 * One animation: its rate, frame count and one strip per direction, and, for a strip whose strike lands on a frame of
 * its own, that frame, `impact` (ADR-095's amendment of 2026-10-10): the page sounds a creature's attack there. A
 * strip whose art already stands above its feet line (a hop, a flight) names how far on each frame, `lift`, in the
 * art's pixels, so the ground shadow under it can narrow with the height (P2.22-T38).
 */
export interface SheetAnim {
  readonly fps: number;
  readonly frames: number;
  readonly files: Readonly<Record<DirectionName, string>>;
  readonly impact?: number;
  readonly lift?: readonly number[];
}

/**
 * A sheet: frame size, the pivot that stands on the entity's position, its animations and, for a speaker whose dialogue
 * window animates its face, the portrait's clip table (`portrait-clips.ts`), whose `url` the build sets.
 */
export interface SheetDescription {
  readonly frameWidth: number;
  readonly frameHeight: number;
  readonly pivot: { readonly x: number; readonly y: number };
  readonly anims: Readonly<Record<string, SheetAnim>>;
  readonly portrait?: PortraitClips;
}

/**
 * Reads a positive integer field.
 *
 * @param object The holder.
 * @param key Field name.
 * @param where Path for the error.
 * @returns The value.
 * @throws Error when the field is not a positive integer.
 */
function positiveInt(object: Record<string, unknown>, key: string, where: string): number {
  const value = object[key];
  if (typeof value !== "number" || !Number.isInteger(value) || value <= 0) {
    throw new Error(`${where}.${key} must be a positive integer`);
  }
  return value;
}

/**
 * Narrows to a plain object.
 *
 * @param value The value.
 * @param where Path for the error.
 * @returns The object.
 * @throws Error when the value is not an object.
 */
function asObject(value: unknown, where: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${where} must be an object`);
  }
  return value as Record<string, unknown>;
}

/**
 * Validates one animation.
 *
 * @param value The parsed animation.
 * @param where Path for the error.
 * @returns The animation.
 * @throws Error when a field is missing or malformed, or `impact` is given and is not a frame index of the strip.
 */
function validateAnim(value: unknown, where: string): SheetAnim {
  const anim = asObject(value, where);
  const fps = positiveInt(anim, "fps", where);
  const frames = positiveInt(anim, "frames", where);
  const files = asObject(anim["files"], `${where}.files`);
  const named: Partial<Record<DirectionName, string>> = {};
  for (const direction of DIRECTION_NAMES) {
    const file = files[direction];
    if (typeof file !== "string" || file === "") {
      throw new Error(`${where}.files.${direction} must be a file name`);
    }
    named[direction] = file;
  }
  const base = { fps, frames, files: named as Record<DirectionName, string> };
  const lift = validateLift(anim["lift"], frames, where);
  const impact = anim["impact"];
  if (impact === undefined) {
    return lift === undefined ? base : { ...base, lift };
  }
  if (typeof impact !== "number" || !Number.isInteger(impact) || impact < 0 || impact >= frames) {
    throw new Error(`${where}.impact must be a frame index below ${frames}`);
  }
  return lift === undefined ? { ...base, impact } : { ...base, impact, lift };
}

/**
 * Validates a strip's frame lifts.
 *
 * @param value The parsed `lift`, undefined when the strip names none.
 * @param frames The strip's frame count.
 * @param where Path for the error.
 * @returns The lifts, or undefined for none.
 * @throws Error when `lift` is not one non-negative integer per frame.
 */
function validateLift(value: unknown, frames: number, where: string): readonly number[] | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (!Array.isArray(value) || value.length !== frames || value.some((lift) => typeof lift !== "number" || !Number.isInteger(lift) || lift < 0)) {
    throw new Error(`${where}.lift must hold ${frames} non-negative integers`);
  }
  return value as number[];
}

/**
 * Validates a parsed `sheet.json`. The pivot may sit on any edge of the frame, both axes inclusive, since
 * an origin of exactly 0 or 1 is a valid sprite origin; a sheet's feet row may be one line below its
 * lowest opaque pixel.
 *
 * @param value The parsed JSON.
 * @param required The animations the sheet must hold; `REQUIRED_ANIMS` unless a caller admits a sheet that only
 *   stands (`STANDING_ANIMS`).
 * @returns The typed description.
 * @throws Error naming the first field that is missing or malformed, or a required animation that is absent.
 */
export function validateSheet(value: unknown, required: readonly string[] = REQUIRED_ANIMS): SheetDescription {
  const sheet = asObject(value, "sheet");
  const frameWidth = positiveInt(sheet, "frameWidth", "sheet");
  const frameHeight = positiveInt(sheet, "frameHeight", "sheet");
  const pivotObject = asObject(sheet["pivot"], "sheet.pivot");
  const pivot = { x: pivotObject["x"], y: pivotObject["y"] };
  if (typeof pivot.x !== "number" || typeof pivot.y !== "number" || !Number.isInteger(pivot.x) || !Number.isInteger(pivot.y) || pivot.x < 0 || pivot.y < 0 || pivot.x > frameWidth || pivot.y > frameHeight) {
    throw new Error("sheet.pivot must lie within the frame, edges included");
  }
  const animsObject = asObject(sheet["anims"], "sheet.anims");
  const anims: Record<string, SheetAnim> = {};
  for (const [name, anim] of Object.entries(animsObject)) {
    anims[name] = validateAnim(anim, `sheet.anims.${name}`);
  }
  for (const name of required) {
    if (anims[name] === undefined) {
      throw new Error(`sheet.anims.${name} is required`);
    }
  }
  const base = { frameWidth, frameHeight, pivot: { x: pivot.x, y: pivot.y }, anims };
  return sheet["portrait"] === undefined ? base : { ...base, portrait: validatePortraitClips(sheet["portrait"]) };
}

/**
 * Whether a sheet has the strip a moving character walks by.
 *
 * @param sheet The sheet.
 * @returns True when it holds a `walk` animation.
 */
export function sheetCanWalk(sheet: Pick<SheetDescription, "anims">): boolean {
  return sheet.anims["walk"] !== undefined;
}

/**
 * The frame an animation shows at a time since it started.
 *
 * @param anim The animation.
 * @param elapsedMs Milliseconds since the animation started; negative counts as zero.
 * @returns Frame index in `[0, anim.frames)`.
 */
export function frameIndex(anim: SheetAnim, elapsedMs: number): number {
  const ms = elapsedMs > 0 ? elapsedMs : 0;
  return Math.floor((ms * anim.fps) / 1000) % anim.frames;
}

/**
 * The direction key of a facing.
 *
 * @param facing PROTOCOL facing.
 * @returns The key.
 */
export function directionName(facing: Facing): DirectionName {
  return DIRECTION_NAMES[facing];
}

/**
 * The locomotion animation for whether the entity moved since the last frame (ADR-036 item 3).
 *
 * @param moving Whether the rendered position changed.
 * @returns `walk` or `idle`.
 */
export function motionAnim(moving: boolean): "idle" | "walk" {
  return moving ? "walk" : "idle";
}

/**
 * The strip a moving character plays while it runs (ADR-116 decisions 14 and 21). The character pack has no run with
 * the weapon drawn, so there is one, with the sword and shield on the back, whatever the stance.
 */
export const RUN_STRIP = "run";

/**
 * The strip a flying character plays while it moves with the running bit (P2.22-T38), as `run` is played on a sheet
 * that has no `fly`.
 */
export const FLY_STRIP = "fly";

/**
 * The strip played once as the flight begins, before `fly`.
 */
export const TAKEOFF_STRIP = "takeoff";

/**
 * The strip played once as the flight ends, over the spot the character stops on.
 */
export const LAND_STRIP = "land";

/**
 * The suffix of the strips a character stands and walks by while its weapon is drawn: `idle_drawn` and `walk_drawn`.
 */
export const DRAWN_SUFFIX = "_drawn";

/**
 * The drawn strips' names, made once: a character asks for its strip every frame.
 */
const STANDING_DRAWN = `${motionAnim(false)}${DRAWN_SUFFIX}`;
const MOVING_DRAWN = `${motionAnim(true)}${DRAWN_SUFFIX}`;

/**
 * The strip a character's motion plays, the one choice the page, a watcher's page and any tool share (ADR-036
 * decision 3, ADR-094 decision 22, ADR-116 decisions 14 and 21):
 *
 * - not moving: `idle`, or `idle_drawn` while its weapon is drawn and the sheet has it, whatever the running bit says;
 * - moving with the running bit, on a sheet that has `fly`: `fly`; else on a sheet that has `run`: `run`, whatever its stance;
 * - moving otherwise, and running on a sheet without `run`, as a creature's is: `walk`, or `walk_drawn` while its
 *   weapon is drawn and the sheet has it.
 *
 * A one-shot strip (a swing, a hurt, the death held) is the playback's and shows over this; motion's strip returns
 * when it ends. Allocates nothing.
 *
 * @param anims The strips the character can play, by name.
 * @param moving Whether the character moved since the last frame.
 * @param running Whether the map server says it runs: the running bit of its view, or the page's own prediction of it
 *   for its own character. Never a measure of its speed.
 * @param drawn Whether its weapon is drawn.
 * @returns The strip's name.
 *
 * @example
 * locomotionStrip(sheet.anims, true, true, true); // "run"
 * locomotionStrip(creature.anims, true, true, false); // "walk"
 */
export function locomotionStrip(anims: Readonly<Record<string, SheetAnim>>, moving: boolean, running: boolean, drawn: boolean): string {
  if (moving && running) {
    if (anims[FLY_STRIP] !== undefined) {
      return FLY_STRIP;
    }
    if (anims[RUN_STRIP] !== undefined) {
      return RUN_STRIP;
    }
  }
  const plain = motionAnim(moving);
  if (!drawn) {
    return plain;
  }
  const variant = moving ? MOVING_DRAWN : STANDING_DRAWN;
  return anims[variant] === undefined ? plain : variant;
}
